/**
 * Collaboration types for NoteSense real-time collaboration feature
 */

/**
 * Connection state for WebSocket
 */
export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting' | 'error';

// ============================================================
// Shape Locking Types
// ============================================================

/**
 * Lock type for shape locking
 */
export type LockType = 'drag' | 'resize' | 'edit' | 'full';

/**
 * Shape lock information
 */
export interface ShapeLock {
  /** ID of the locked shape */
  shapeId: string;
  /** User ID who locked the shape */
  userId: string;
  /** User name for display */
  userName: string;
  /** User color for visual indicator */
  userColor: string;
  /** Type of lock */
  lockType: LockType;
  /** Timestamp when lock was acquired */
  lockedAt: number;
  /** Timestamp when lock expires (auto-release) */
  expiresAt: number;
}

/**
 * Lock request message
 */
export interface LockRequestMessage extends BaseMessage {
  type: 'lock_request';
  roomId: string;
  userId: string;
  shapeId: string;
  lockType: LockType;
}

/**
 * Lock granted message
 */
export interface LockGrantedMessage extends BaseMessage {
  type: 'lock_granted';
  roomId: string;
  shapeId: string;
  lock: ShapeLock;
}

/**
 * Lock denied message
 */
export interface LockDeniedMessage extends BaseMessage {
  type: 'lock_denied';
  roomId: string;
  shapeId: string;
  reason: string;
  currentLock?: ShapeLock;
}

/**
 * Lock release message
 */
export interface LockReleaseMessage extends BaseMessage {
  type: 'lock_release';
  roomId: string;
  userId: string;
  shapeId: string;
}

/**
 * Lock released broadcast
 */
export interface LockReleasedMessage extends BaseMessage {
  type: 'lock_released';
  roomId: string;
  shapeId: string;
}

/**
 * Lock update broadcast (new lock or lock expired)
 */
export interface LockUpdateMessage extends BaseMessage {
  type: 'lock_update';
  roomId: string;
  shapeId: string;
  lock: ShapeLock | null;
}

/**
 * Default lock duration (5 seconds for drag operations)
 */
export const DEFAULT_LOCK_DURATION = 5000;

/**
 * Lock duration by type
 */
export const LOCK_DURATIONS: Record<LockType, number> = {
  drag: 5000,    // 5 seconds
  resize: 5000,  // 5 seconds
  edit: 30000,   // 30 seconds
  full: 60000,   // 60 seconds
};

/**
 * Collaboration mode
 */
export type CollaborationMode = 'local' | 'collaborative';

/**
 * User cursor position
 */
export interface UserCursor {
  x: number;
  y: number;
}

/**
 * User viewport (canvas view area)
 */
export interface UserViewport {
  x: number;
  y: number;
  scale: number;
}

/**
 * User presence information
 */
export interface UserPresence {
  cursor: UserCursor | null;
  viewport: UserViewport | null;
  selection: string[]; // Selected shape IDs
  lastUpdate: number;
}

/**
 * Collaboration user
 */
export interface CollaborationUser {
  id: string;
  name: string;
  color: string;
  isOnline: boolean;
  presence?: UserPresence;
}

/**
 * Session status
 */
export type SessionStatus = 'active' | 'paused' | 'closed';

/**
 * Collaboration session
 */
export interface CollaborationSession {
  id: string;
  name: string;
  ownerId: string | null;
  status: SessionStatus;
  userCount: number;
  maxUsers: number;
  isPublic: boolean;
  createdAt: number;
}

/**
 * Collaboration state
 */
export interface CollaborationState {
  mode: CollaborationMode;
  connectionState: ConnectionState;
  session: CollaborationSession | null;
  currentUser: CollaborationUser | null;
  users: CollaborationUser[];
  error: string | null;
}

/**
 * Shape data for sync (mirrors server type)
 */
export interface ShapeData {
  id: string;
  type: string;
  x: number;
  y: number;
  width?: number;
  height?: number;
  [key: string]: unknown;
}

/**
 * Room state from server
 */
export interface RoomState {
  shapes: ShapeData[];
  version: number;
  lastModified: number;
  lastModifiedBy: string | null;
}

/**
 * Message types (client side)
 */
export type MessageType =
  | 'join'
  | 'joined'
  | 'leave'
  | 'user_joined'
  | 'user_left'
  | 'update'
  | 'cursor'
  | 'cursor_update'
  | 'selection'
  | 'selection_update'
  | 'sync'
  | 'sync_response'
  | 'error'
  | 'ping'
  | 'pong'
  | 'lock_request'
  | 'lock_granted'
  | 'lock_denied'
  | 'lock_release'
  | 'lock_released'
  | 'lock_update'
  | 'kick'
  | 'kicked';

/**
 * Base message interface
 */
export interface BaseMessage {
  type: MessageType;
  timestamp: number;
}

/**
 * Join room message
 */
export interface JoinMessage extends BaseMessage {
  type: 'join';
  roomId: string;
  userId: string;
  userName: string;
}

/**
 * Join confirmation message
 */
export interface JoinedMessage extends BaseMessage {
  type: 'joined';
  roomId: string;
  userId: string;
  users: CollaborationUser[];
  state: RoomState;
}

/**
 * User joined notification
 */
export interface UserJoinedMessage extends BaseMessage {
  type: 'user_joined';
  roomId: string;
  user: {
    id: string;
    name: string;
    color: string;
  };
}

/**
 * User left notification
 */
export interface UserLeftMessage extends BaseMessage {
  type: 'user_left';
  roomId: string;
  userId: string;
}

/**
 * Leave room message
 */
export interface LeaveMessage extends BaseMessage {
  type: 'leave';
  roomId: string;
  userId: string;
}

/**
 * Update action types
 */
export type UpdateAction = 'add' | 'update' | 'delete' | 'bulk';

/**
 * Update data
 */
export interface UpdateData {
  action: UpdateAction;
  shapes?: ShapeData[];
  shapeId?: string;
  shapeIds?: string[];
}

/**
 * Canvas update message
 */
export interface UpdateMessage extends BaseMessage {
  type: 'update';
  roomId: string;
  userId: string;
  data: UpdateData;
}

/**
 * Cursor position message
 */
export interface CursorMessage extends BaseMessage {
  type: 'cursor';
  roomId: string;
  userId: string;
  cursor: UserCursor | null;
}

/**
 * Cursor update broadcast
 */
export interface CursorUpdateMessage extends BaseMessage {
  type: 'cursor_update';
  roomId: string;
  userId: string;
  cursor: UserCursor | null;
}

/**
 * Selection message (client to server)
 */
export interface SelectionMessage extends BaseMessage {
  type: 'selection';
  roomId: string;
  userId: string;
  selection: string[]; // Selected shape IDs
}

/**
 * Selection update broadcast (server to clients)
 */
export interface SelectionUpdateMessage extends BaseMessage {
  type: 'selection_update';
  roomId: string;
  userId: string;
  selection: string[];
}

/**
 * Sync request message
 */
export interface SyncRequestMessage extends BaseMessage {
  type: 'sync';
  roomId: string;
  userId: string;
}

/**
 * Sync response message
 */
export interface SyncResponseMessage extends BaseMessage {
  type: 'sync_response';
  roomId: string;
  state: RoomState;
  users: CollaborationUser[];
}

/**
 * Error message
 */
export interface ErrorMessage extends BaseMessage {
  type: 'error';
  error: string;
  code?: string;
}

/**
 * Ping message
 */
export interface PingMessage extends BaseMessage {
  type: 'ping';
}

/**
 * Pong message
 */
export interface PongMessage extends BaseMessage {
  type: 'pong';
}

/**
 * Kick user message (client to server)
 */
export interface KickMessage extends BaseMessage {
  type: 'kick';
  roomId: string;
  userId: string; // User requesting the kick
  targetUserId: string; // User to be kicked
}

/**
 * Kicked notification (server to kicked client)
 */
export interface KickedMessage extends BaseMessage {
  type: 'kicked';
  roomId: string;
  kickedBy: string; // Name of user who kicked
}

/**
 * Union type of all messages
 */
export type Message =
  | JoinMessage
  | JoinedMessage
  | LeaveMessage
  | UserJoinedMessage
  | UserLeftMessage
  | UpdateMessage
  | CursorMessage
  | CursorUpdateMessage
  | SelectionMessage
  | SelectionUpdateMessage
  | SyncRequestMessage
  | SyncResponseMessage
  | ErrorMessage
  | PingMessage
  | PongMessage
  | LockRequestMessage
  | LockGrantedMessage
  | LockDeniedMessage
  | LockReleaseMessage
  | LockReleasedMessage
  | LockUpdateMessage
  | KickMessage
  | KickedMessage;

/**
 * WebSocket hook options
 */
export interface WebSocketOptions {
  url: string;
  autoConnect?: boolean;
  reconnect?: boolean;
  maxReconnectAttempts?: number;
  reconnectInterval?: number;
  pingInterval?: number;
}

/**
 * WebSocket hook return type
 */
export interface UseWebSocketReturn {
  connectionState: ConnectionState;
  connect: (urlOverride?: string) => void;
  disconnect: () => void;
  send: (message: Message) => void;
  lastMessage: Message | null;
  error: string | null;
}

/**
 * Collaboration context value
 */
export interface CollaborationContextValue {
  // State
  state: CollaborationState;

  // Session operations
  createSession: (name: string) => void;
  joinSession: (sessionId: string, userName: string) => void;
  leaveSession: () => void;
  kickUser: (targetUserId: string) => void;

  // Mode operations
  setMode: (mode: CollaborationMode) => void;

  // Sync operations
  sendUpdate: (data: UpdateData) => void;
  sendCursor: (cursor: UserCursor | null) => void;
  sendSelection: (selection: string[]) => void;

  // Lock operations
  lockedShapes: Map<string, ShapeLock>;
  requestLock: (shapeId: string, lockType: LockType) => void;
  releaseLock: (shapeId: string) => void;
  isShapeLocked: (shapeId: string) => boolean;
  isShapeLockedByMe: (shapeId: string) => boolean;
  getShapeLock: (shapeId: string) => ShapeLock | null;

  // Event handlers (for external components)
  onShapesUpdate?: (shapes: ShapeData[]) => void;

  // Remote shapes received from other users
  remoteShapes: ShapeData[] | null;

  // Function to check if user is voluntarily leaving session
  isLeavingSession: () => boolean;

  // Remote update with action (for incremental updates)
  remoteUpdate: {
    action: string;
    shapes?: ShapeData[];
    shapeIds?: string[];
    updateId: number;
  } | null;
}

/**
 * Generate a random user color
 */
export function generateUserColor(): string {
  const colors = [
    '#EF4444', // red
    '#F97316', // orange
    '#F59E0B', // amber
    '#84CC16', // lime
    '#22C55E', // green
    '#14B8A6', // teal
    '#06B6D4', // cyan
    '#3B82F6', // blue
    '#6366F1', // indigo
    '#8B5CF6', // violet
    '#A855F7', // purple
    '#EC4899', // pink
  ];
  return colors[Math.floor(Math.random() * colors.length)];
}

/**
 * Generate a random user ID
 */
export function generateUserId(): string {
  return `user_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Generate a random session ID
 */
export function generateSessionId(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 8; i++) {
    code += chars[Math.floor(Math.random() * chars.length)];
  }
  return code;
}
